import React, { useEffect, useState, useRef } from "react";
import { useParams, Link } from "react-router-dom";
import api, {API, authFetch } from "@/lib/api";
import CassettaDelMezzo from "@/components/CassettaDelMezzo";
import QRBox from "@/components/QRBox";
import { Card, CardContent } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "@/components/ui/tabs";
import { BarChart, Bar, XAxis, YAxis, Tooltip, ResponsiveContainer, CartesianGrid } from "recharts";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { useAuth } from "@/context/AuthContext";
import { fileUrl } from "@/lib/placeholders";
import { vedeICosti, euro, CADENZE, FASCE_MINUTI, PASSO_MINUTI, durataLeggibile, fraMesi } from "@/lib/costi";
import { ControlloInternoDialog, ModificaChecklistDialog } from "@/components/ControlloInterno";
import LetturaContatore from "@/components/LetturaContatore";
import NewReportDialog from "@/components/NewReportDialog";
import CampoUtilizzatore from "@/components/CampoUtilizzatore";
import { puoGestire } from "@/lib/ruoli";
import BottoneRaccogli, { BottoneRaccogliTutti } from "@/components/BottoneRaccogli";
import Briciole from "@/components/Briciole";
import BottoneFoto from "@/components/BottoneFoto";
import { toast } from "sonner";
import {
  Upload, FileText, Archive, Trash2, Plus, QrCode, ExternalLink, Wrench, Truck,
  BookOpen,
  AlertTriangle, Edit, Zap, Gauge, Construction, Radio, FileUp, CheckCircle2, XCircle, Download,
} from "lucide-react";

// La data come si legge: la usano il riquadro dei DDT e quello delle
// segnalazioni, quindi sta qui e non dentro uno dei due.
const giorno = (iso) => (iso ? iso.substring(0, 10).split("-").reverse().join("/") : "");

const EQUIPMENT_ICON_MAP = {
  AUTOCARRI: Truck, AUTOVETTURE: Truck, FURGONI: Truck, "FURGONI ATTREZZATI": Truck,
  ESCAVATORI: Construction, "MACCHINE SPECIALI": Construction,
  GENERATORI: Zap, TAGLIASFALTO: Construction,
  ESPLOSIMETRI: Radio, CERCAFUGHE: Radio,
  PEAD: Wrench, FILIERE: Wrench, RAVETTI: Wrench, PESTELLO: Wrench, BATTIASFALTO: Wrench,
  "STRUMENTI DI COLLAUDO": Gauge,
};
function EquipmentIcon({ category, className }) {
  const Cmp = EQUIPMENT_ICON_MAP[category] || Wrench;
  return <Cmp className={className} />;
}

const DOC_TYPES = [
  { value: "ce", label: "Documentazione" },
  { value: "maintenance", label: "Documento manutenzione" },
  { value: "logbook", label: "Libro manutenzione" },
  // I certificati di taratura degli strumenti di misura stanno per conto
  // loro: sono quelli che in verifica vengono chiesti per primi, e dentro
  // «Documentazione» finivano in mezzo a libretti e assicurazioni.
  { value: "taratura", label: "Taratura" },
  // I due documenti dei mezzi targati. Separati perche' la revisione scade e
  // va monitorata, mentre il libretto resta finche' non cambia qualcosa.
  { value: "libretto", label: "Libretto di circolazione" },
  { value: "revisione", label: "Revisione" },
  // Tre caselle non bastano: una macchina si porta dietro anche il libretto
  // di circolazione, l'assicurazione, la fattura d'acquisto, il verbale di
  // furto. Con «Altro» si scrive cos'è, invece di infilarlo in una casella
  // che non c'entra e non ritrovarlo più.
  { value: "altro", label: "Altro (specificare)" },
];

/**
 * Dove si trova il mezzo, quando esce con un documento di trasporto.
 *
 * Risponde alle due domande che ci si fa davanti a un mezzo che non c'è: da
 * chi è andato e quando. Il documento si apre, così si vede tutto il resto —
 * causale, righe, chi ha firmato.
 *
 * «È rientrato» rimette il mezzo esattamente nello stato in cui era prima di
 * uscire: uno già fermo per manutenzione torna fermo, non attivo. Il
 * documento resta nel suo storico, perché è successo.
 */
function FuoriConDDT({ eq, onCambiato }) {
  const [storico, setStorico] = useState([]);
  const [lavoro, setLavoro] = useState(false);
  const { user } = useAuth();
  const puoRientrare = ["admin", "amministrativo", "tecnico"].includes(user?.role);
  const uscita = eq.ddt_uscita;

  useEffect(() => {
    let vivo = true;
    api.get(`/ddt?equipment_id=${eq.id}`)
      .then(({ data }) => { if (vivo) setStorico(data.ddt || []); })
      .catch(() => {});
    return () => { vivo = false; };
  }, [eq.id]);

  const rientro = async () => {
    if (!window.confirm(
      `Segnare ${eq.identifier} come rientrato?\n\n` +
      "Torna nello stato che aveva prima di uscire. Il documento resta nel suo storico."
    )) return;
    setLavoro(true);
    try {
      await api.post(`/ddt/${uscita.ddt_id}/rientro?equipment_id=${eq.id}`);
      toast.success("Rientrato");
      onCambiato();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Errore");
    } finally { setLavoro(false); }
  };

  if (!uscita && storico.length === 0) return null;

  return (
    <Card className={uscita ? "border-amber-300 bg-amber-50" : "border-slate-200"}
          data-testid="mezzo-ddt">
      <CardContent className="p-4 space-y-3">
        {uscita ? (
          <div className="flex items-start gap-3 flex-wrap">
            <Truck className="w-5 h-5 text-amber-600 flex-shrink-0 mt-0.5" />
            <div className="flex-1 min-w-0">
              <div className="font-display font-bold text-amber-900">
                Fuori sede{uscita.nota_stato ? `, ${uscita.nota_stato}` : ""}
              </div>
              <div className="text-sm text-amber-900/80">
                Da <strong>{uscita.cliente || "—"}</strong>
                {uscita.destinazione && uscita.destinazione !== "IDEM"
                  && ` (${uscita.destinazione})`}
                {uscita.data && ` dal ${giorno(uscita.data)}`}
              </div>
              <div className="text-xs text-amber-900/60 mt-0.5">
                {uscita.causale}
                {uscita.descrizione && ` · ${uscita.descrizione}`}
              </div>
              <Link to="/ddt" className="text-xs text-[#0067b2] font-medium">
                Documento di trasporto {uscita.numero} →
              </Link>
            </div>
            {puoRientrare && (
              <Button size="sm" variant="outline" onClick={rientro} disabled={lavoro}
                      data-testid="mezzo-rientro" className="bg-white">
                È rientrato
              </Button>
            )}
          </div>
        ) : (
          <div className="text-sm font-semibold flex items-center gap-2">
            <Truck className="w-4 h-4 text-slate-400" /> Documenti di trasporto
          </div>
        )}

        {storico.length > 0 && (
          <div className="border-t border-black/5 pt-2">
            <div className="text-[10px] uppercase tracking-widest text-slate-500 mb-1">
              È passato su {storico.length}{" "}
              {storico.length === 1 ? "documento" : "documenti"}
            </div>
            <div className="max-h-40 overflow-y-auto space-y-1">
              {storico.map((d) => (
                <div key={d.id} className="text-xs flex items-baseline gap-2">
                  <span className="font-semibold tabular-nums w-16 flex-shrink-0">{d.numero}</span>
                  <span className="tabular-nums text-slate-500 w-20 flex-shrink-0">{giorno(d.data)}</span>
                  <span className="flex-1 min-w-0 truncate">
                    {d.cliente}{d.causale ? ` · ${d.causale}` : ""}
                  </span>
                  <span className="text-slate-400 flex-shrink-0">{d.autore}</span>
                </div>
              ))}
            </div>
          </div>
        )}
      </CardContent>
    </Card>
  );
}

/**
 * Le segnalazioni aperte su questa macchina.
 *
 * Un guasto segnalato dal campo e la manutenzione che lo ripara sono la stessa
 * storia: se la segnalazione resta solo nell'elenco generale, chi apre la
 * scheda del mezzo per programmare l'intervento non sa che qualcuno l'ha già
 * detto. Qui si vede subito, e da qui si scrive la nuova.
 */
function SegnalazioniDelMezzo({ eq }) {
  const [righe, setRighe] = useState([]);
  const [nuova, setNuova] = useState(false);

  const carica = React.useCallback(() => {
    if (!eq?.id) return;
    api.get(`/reports?status=all&equipment_id=${eq.id}`)
      .then(({ data }) => setRighe(data)).catch(() => {});
  }, [eq?.id]);
  useEffect(() => { carica(); }, [carica]);

  const aperte = righe.filter((r) => r.status !== "chiusa");

  return (
    <Card className={aperte.length ? "border-red-200 bg-red-50/40" : "border-slate-200"}>
      <CardContent className="p-4 space-y-2">
        <div className="flex items-center gap-2">
          <AlertTriangle className={`w-4 h-4 ${aperte.length ? "text-red-500" : "text-slate-400"}`} />
          <span className="text-sm font-semibold">
            {aperte.length
              ? `${aperte.length} ${aperte.length === 1 ? "segnalazione aperta" : "segnalazioni aperte"}`
              : "Nessuna segnalazione aperta"}
          </span>
          <Button size="sm" variant="outline" className="ml-auto bg-white"
                  onClick={() => setNuova(true)} data-testid="segnala-guasto">
            Segnala un difetto
          </Button>
        </div>

        {righe.length > 0 && (
          <div className="max-h-40 overflow-y-auto space-y-1 pt-1">
            {righe.map((r) => (
              <Link key={r.id} to="/segnalazioni"
                    className="text-xs flex items-baseline gap-2 hover:underline">
                <span className={`w-24 flex-shrink-0 font-semibold ${
                  r.status === "chiusa" ? "text-emerald-600" : "text-red-600"}`}>
                  {r.status === "chiusa" ? "chiusa" : r.status === "presa_in_carico"
                    ? "in lavorazione" : "aperta"}
                </span>
                <span className="tabular-nums text-slate-500 w-20 flex-shrink-0">
                  {giorno(r.created_at)}
                </span>
                <span className="flex-1 min-w-0 truncate">{r.description}</span>
              </Link>
            ))}
          </div>
        )}

        {nuova && (
          <NewReportDialog defaultEquipmentId={eq.id}
                           onClose={() => setNuova(false)}
                           onSaved={() => { setNuova(false); carica(); }} />
        )}
      </CardContent>
    </Card>
  );
}

export default function AttrezzaturaDetail() {
  const { id } = useParams();
  const { user } = useAuth();
  const isAdmin = user?.role === "admin";
  // Togliere una macchina, archiviare un documento e rifare l'elenco dei
  // controlli sono `require_gestione` sul server: li fa anche
  // l'amministrazione, che e' poi chi riceve libretti e revisioni.
  const gestisce = puoGestire(user);
  // Ospite e monitor sono di sola lettura; tutti gli altri lavorano sui mezzi
  // e possono registrare interventi e controlli.
  const canLog = !["guest", "monitor"].includes(user?.role);
  // L'import dello storico passa da require_tecnico: mostrarlo a chi poi si
  // becca un 403 e' peggio che non mostrarlo.
  const canImport = ["admin", "amministrativo", "tecnico"].includes(user?.role);
  const vedeCosti = vedeICosti(user);
  // Gli storici importati dai libri cartacei portano dentro le sbavature
  // della carta: la stessa riga scritta due volte con un trattino diverso,
  // il fornitore su una copia e non sull'altra. Si correggono a mano.
  // Gli stessi che possono correggere le righe di manutenzione correggono
  // anche la scheda della macchina - targa sbagliata, modello, stato: e' il
  // tecnico che ce l'ha davanti. Toglierla dall'elenco resta dell'amministratore.
  const puoCorreggere = canImport;
  // Caricare un documento - la taratura di uno strumento, la revisione di un
  // autocarro - lo fa anche il tecnico, che e' quello che ce l'ha in mano:
  // sul server la rotta e' `require_tecnico`.
  const puoCaricareDocumenti = canImport;
  const puoEliminare = ["admin", "amministrativo"].includes(user?.role);
  const [eq, setEq] = useState(null);
  const [docs, setDocs] = useState([]);
  const [logs, setLogs] = useState([]);
  const [showQR, setShowQR] = useState(false);
  const [showUpload, setShowUpload] = useState(false);
  const [showMnt, setShowMnt] = useState(false);
  const [showImportDocx, setShowImportDocx] = useState(false);
  const [costsByYear, setCostsByYear] = useState([]);
  const [yearDetail, setYearDetail] = useState(null);
  const [showEdit, setShowEdit] = useState(false);
  const [showControllo, setShowControllo] = useState(false);
  // Dal QR: «Registra controllo» apre subito la lista di riscontro.
  useEffect(() => {
    try {
      if (new URLSearchParams(window.location.search).get("azione") === "controllo" && eq) setShowControllo(true);
    } catch { /* niente */ }
  }, [eq]);
  const [showChecklist, setShowChecklist] = useState(false);
  const [importoDa, setImportoDa] = useState(null);
  const [correggi, setCorreggi] = useState(null);
  const [cats, setCats] = useState([]);

  const load = async () => {
    const e = await api.get(`/equipment`).then(r => r.data.find(x => x.id === id));
    setEq(e);
    setDocs((await api.get(`/equipment/${id}/documents`)).data);
    setLogs((await api.get(`/equipment/${id}/maintenance`)).data);
  };
  useEffect(() => {
    load();
    api.get("/equipment/categories").then(({data}) => setCats(data));
    if (vedeICosti(user)) {
      api.get(`/equipment/${id}/maintenance/costs-by-year`)
        .then(({data}) => setCostsByYear(data)).catch(() => {});
    }
    /* eslint-disable-next-line */
  }, [id]);

  // Il libro di manutenzione di questa macchina: quello che una volta era il
  // fascicolo Word sul disco di rete, adesso ristampato dai dati.
  const scaricaLibro = async () => {
    try {
      const r = await authFetch(`/equipment/${id}/maintenance/libro-pdf`);
      if (!r.ok) throw new Error();
      // Il nome buono lo manda il server. Se per qualche motivo non si
      // riesce a leggerlo, si rifà qui con la targa: un file che si chiama
      // sempre uguale, in una cartella di download, si perde.
      const cd = r.headers.get("content-disposition") || "";
      const targa = (eq?.identifier || "mezzo").replace(/[^A-Za-z0-9]+/g, "_");
      const nome = /filename="?([^";]+)"?/.exec(cd)?.[1]
        || `Libro_Manutenzione_${targa}.pdf`;
      const u = window.URL.createObjectURL(await r.blob());
      const a = document.createElement("a");
      a.href = u; a.download = nome; a.click();
      window.URL.revokeObjectURL(u);
    } catch { toast.error("Errore nel download del libro"); }
  };

  const onDelete = async () => {
    if (!window.confirm("Eliminare definitivamente questa attrezzatura?")) return;
    await api.delete(`/equipment/${id}`);
    toast.success("Attrezzatura eliminata");
    window.location.href = "/attrezzature";
  };

  const onEditSubmit = async (data) => {
    await api.put(`/equipment/${id}`, data);
    toast.success("Aggiornata");
    setShowEdit(false);
    load();
  };

  const eliminaIntervento = async (l) => {
    if (!window.confirm(
      "Eliminare questo intervento?\n\n"
      + `${l.date?.substring(0, 10)} · ${l.description || ""}\n\nNon si annulla.`
    )) return;
    try {
      await api.delete(`/equipment/maintenance/${l.id}`);
      toast.success("Intervento eliminato");
      load();
    } catch (err) { toast.error(err.response?.data?.detail || "Errore"); }
  };

  const onDocDelete = async (docId) => {
    if (!window.confirm("Eliminare definitivamente il documento?")) return;
    await api.delete(`/equipment/documents/${docId}`);
    toast.success("Documento eliminato");
    load();
  };

  if (!eq) return <div className="text-slate-500">Caricamento...</div>;

  const today = new Date().toISOString().substring(0,10);
  const nd = eq.next_maintenance_date;
  const overdue = nd && nd < today;

  const active = docs.filter(d => !d.archived);
  const archived = docs.filter(d => d.archived);

  // I documenti con la data di scadenza addosso - revisione, assicurazione,
  // taratura di uno strumento - avvisano come la manutenzione. Prima la data
  // stava scritta sulla riga del documento e non la guardava nessuno.
  const fra60 = new Date(Date.now() + 60 * 86400000).toISOString().substring(0, 10);
  const inScadenza = active
    .filter(d => d.expires_date && d.expires_date.substring(0, 10) <= fra60)
    .sort((a, b) => a.expires_date.localeCompare(b.expires_date));
  const docScaduti = inScadenza.filter(d => d.expires_date.substring(0, 10) < today);

  // Gli stessi tre numeri della pagina "Costi manutenzione", qui riferiti alla
  // sola macchina: il totale speso da quando e' in azienda, quanti interventi
  // e su quanti anni. Si ricavano dai costi per anno gia' caricati.
  const totaleCosti = costsByYear.reduce((s, a) => s + a.total_cost, 0);
  const totaleInterventi = costsByYear.reduce((s, a) => s + a.count, 0);
  const byType = (t) => active.filter(d => d.doc_type === t);

  // Come il documento si presenta nel raccoglitore: la cartella e' la targa
  // con marca e modello, che e' il modo in cui il mezzo si nomina fuori.
  const nomeMezzo = [eq.identifier, [eq.brand, eq.model].filter(Boolean).join(" ")]
    .filter(Boolean).join(" ") || "Mezzo";
  const voceRaccoglitore = (d) => ({
    tipo: "mezzo", id: d.id, gruppo: nomeMezzo,
    titolo: d.title || d.doc_type,
    sottotitolo: DOC_TYPES.find(x => x.value === d.doc_type)?.label || d.doc_type,
  });

  return (
    <div className="space-y-6" data-testid="eq-detail-page">
      {/* Il percorso intero: la categoria e' un collegamento, cosi' chi sta
          lavorando sugli strumenti di collaudo torna li' e non in cima
          all'elenco di tutto il parco. */}
      <Briciole voci={[
        { testo: "Mezzi e attrezzature", a: "/attrezzature" },
        eq.category && { testo: eq.category,
                         a: `/attrezzature?categoria=${encodeURIComponent(eq.category)}` },
        { testo: [eq.brand, eq.model, eq.tipo].filter(Boolean).join(" ") || eq.identifier },
      ]} />

      <CassettaDelMezzo eqId={id} />

      <Card className="border-slate-200">
        <CardContent className="p-6">
          <div className="flex flex-col lg:flex-row items-start gap-4">
            {eq.photo_url
              ? <img src={`${process.env.REACT_APP_BACKEND_URL}/api${eq.photo_url}`} alt="" className="w-28 h-28 lg:w-32 lg:h-32 rounded object-cover border border-slate-200 flex-shrink-0" />
              : <div className="w-28 h-28 lg:w-32 lg:h-32 rounded bg-orange-50 flex items-center justify-center flex-shrink-0">
                  <EquipmentIcon category={eq.category} className="w-14 h-14 text-orange-500" />
                </div>
            }
            <div className="flex-1 min-w-0 w-full">
              <div className="text-xs uppercase tracking-widest text-orange-600">{eq.category}</div>
              <h1 className="font-display text-2xl sm:text-3xl font-black tracking-tighter break-words">{[eq.brand, eq.model, eq.tipo].filter(Boolean).join(" ") || eq.identifier}</h1>
              <div className="text-xs text-slate-500 mt-1">Matricola/Targa: <span className="font-mono font-semibold text-slate-700">{eq.identifier}</span>{eq.year && <span className="ml-2">· Anno {eq.year}</span>}</div>
              {eq.owner && <div className="text-xs text-slate-500 mt-1">Proprietà: <span className="font-semibold">{eq.owner}</span></div>}
              {eq.utilizzatore && <div className="text-xs text-slate-500 mt-1">Utilizzatore: <span className="font-semibold">{eq.utilizzatore}</span></div>}
              {eq.data_inserimento && (
                <div className="text-xs text-slate-500 mt-1">
                  Inserita in azienda il <span className="font-semibold">{String(eq.data_inserimento).slice(0, 10).split("-").reverse().join("/")}</span>
                  {eq.accessorio && <span className="ml-2">· accessorio di sollevamento / anticaduta</span>}
                </div>
              )}
              {eq.notes && <p className="text-sm text-slate-500 mt-2">{eq.notes}</p>}
              <div className="flex items-center gap-2 mt-3 flex-wrap">
                <Badge>{eq.status === "active" ? "Attiva" : eq.status === "maintenance" ? "In manutenzione" : eq.status === "stolen" ? "Rubata" : eq.status === "scrapped" ? "Smaltita" : eq.status === "sold" ? "Venduta" : "Dismessa"}</Badge>
                {eq.ddt_uscita && (
                  <Badge className="bg-amber-100 text-amber-800 border-0">
                    Fuori sede
                  </Badge>
                )}
                {eq.maintenance_interval_months && <Badge variant="outline">Cadenza {eq.maintenance_interval_months} mesi</Badge>}
                {nd && <Badge className={overdue ? "bg-red-500 text-white border-0" : "bg-emerald-100 text-emerald-700 border-0"}>
                  {overdue ? "Scaduta" : "Prossima"}: {nd.substring(0,10)}
                </Badge>}
                {eq.revisione_mancante && (
                  <Badge className="bg-slate-700 text-white border-0"
                         title="Mezzo targato senza revisione in archivio">
                    Revisione da inserire
                  </Badge>
                )}
                {inScadenza.length > 0 && (
                  <Badge className={docScaduti.length
                    ? "bg-red-500 text-white border-0" : "bg-amber-500 text-white border-0"}>
                    {docScaduti.length
                      ? `${docScaduti.length} document${docScaduti.length === 1 ? "o scaduto" : "i scaduti"}`
                      : `${inScadenza.length} in scadenza`}
                  </Badge>
                )}
              </div>
            </div>
            <div className="flex lg:flex-col gap-2 w-full lg:w-auto flex-wrap">
              <Button variant="outline" size="sm" onClick={() => setShowQR(true)} data-testid="eq-qr-btn" className="flex-1 lg:flex-none"><QrCode className="w-4 h-4 mr-2" />QR</Button>
              <div className="flex gap-1 flex-1 lg:flex-none">
                <Button variant="outline" size="sm" onClick={scaricaLibro}
                        data-testid="eq-libro-btn" className="flex-1"
                        title="Il libro di manutenzione di questa macchina">
                  <BookOpen className="w-4 h-4 mr-2" />Libro
                </Button>
                {/* Il libro non e' un file in archivio: l'app lo scrive al
                    momento. Da qui si mette da parte come gli altri
                    documenti, invece di finire fra i download. */}
                <BottoneRaccogli className="h-9 w-9 p-0"
                                 voce={{ tipo: "generato", id: `libro:${id}`,
                                         gruppo: eq.identifier,
                                         titolo: "Libro di manutenzione",
                                         sottotitolo: [eq.brand, eq.model].filter(Boolean).join(" ") }} />
              </div>
              {canLog && <LetturaContatore eq={eq} onSaved={load} />}
              <Link to={`/attrezzatura/${id}`} target="_blank" className="flex-1 lg:flex-none">
                <Button variant="outline" size="sm" className="w-full"><ExternalLink className="w-4 h-4 mr-2" />Pubblico</Button>
              </Link>
              {puoCorreggere && (<>
                <BottoneFoto url={`/equipment/${id}/photo`} onCaricata={load}
                             etichetta="Foto" testId="eq-foto-btn"
                             classeContenitore="flex-1 lg:flex-none" className="w-full" />
                <Button variant="outline" size="sm" onClick={() => setShowEdit(true)} data-testid="eq-edit-btn" className="flex-1 lg:flex-none"><Edit className="w-4 h-4 mr-2" />Modifica</Button>
              </>)}
              {gestisce && (
                <Button variant="outline" size="sm" onClick={onDelete} data-testid="eq-delete-btn" className="flex-1 lg:flex-none text-red-600 hover:bg-red-50"><Trash2 className="w-4 h-4 mr-2" />Elimina</Button>
              )}
            </div>
          </div>
        </CardContent>
      </Card>

      <FuoriConDDT eq={eq} onCambiato={load} />

      <SegnalazioniDelMezzo eq={eq} />

      <Tabs defaultValue="documents">
        <TabsList className="w-full justify-start overflow-x-auto">
          <TabsTrigger value="documents" data-testid="tab-documents">Documenti</TabsTrigger>
          <TabsTrigger value="maintenance" data-testid="tab-maintenance">Libro manutenzione ({logs.length})</TabsTrigger>
          <TabsTrigger value="archive" data-testid="tab-archive">Archivio ({archived.length})</TabsTrigger>
        </TabsList>

        <TabsContent value="documents" className="space-y-2">
          {inScadenza.length > 0 && (
            <div className={`text-sm rounded border p-2 ${docScaduti.length
              ? "bg-red-50 border-red-200 text-red-800"
              : "bg-amber-50 border-amber-200 text-amber-800"}`}>
              {inScadenza.map(d => (
                <div key={d.id}>
                  <strong>{d.title}</strong>{" "}
                  {d.expires_date.substring(0, 10) < today ? "è scaduto il" : "scade il"}{" "}
                  {d.expires_date.substring(0, 10).split("-").reverse().join("/")}
                </div>
              ))}
            </div>
          )}
          <div className="flex items-center justify-between gap-2 flex-wrap">
            {/* I tipi senza documenti stanno su una riga sola, non ognuno
                in una scatola vuota. */}
            <p className="text-xs text-slate-500 flex-1 min-w-[200px]">
              {DOC_TYPES.filter(t => byType(t.value).length === 0).length > 0 && (
                <>Senza documenti: {DOC_TYPES.filter(t => byType(t.value).length === 0).map(t => t.label).join(", ")}.</>
              )}
            </p>
            <BottoneRaccogliTutti
              etichetta="Tutti nel raccoglitore"
              voci={docs.filter(d => !d.archived && d.file_url).map(d => voceRaccoglitore(d))} />
            {puoCaricareDocumenti && (
              <Button size="sm" onClick={() => setShowUpload(true)} className="bg-orange-500 hover:bg-orange-600 text-white h-8" data-testid="upload-doc-btn">
                <Upload className="w-3 h-3 mr-1" />Carica documento
              </Button>
            )}
          </div>
          {DOC_TYPES.filter(t => byType(t.value).length > 0).map(t => (
            <Card key={t.value} className="border-slate-200">
              <CardContent className="p-3">
                <h3 className="font-display font-bold text-sm mb-1">{t.label}</h3>
                <div className="divide-y divide-slate-100">
                  {byType(t.value).map(d => (
                    <div key={d.id} className="py-1.5 flex items-center gap-2">
                      <FileText className="w-4 h-4 text-orange-500 flex-shrink-0" />
                      <div className="flex-1 min-w-0 flex flex-wrap items-center gap-x-2">
                        <span className="font-medium text-sm truncate">{d.title}</span>
                        <span className="text-xs text-slate-500 truncate">
                          {d.uploaded_at?.substring(0,10).split("-").reverse().join("/")}
                          {d.expires_date && ` → scade ${d.expires_date.substring(0,10).split("-").reverse().join("/")}`}
                        </span>
                      </div>
                      <div className="flex items-center gap-1 flex-shrink-0">
                        {d.file_url && <BottoneRaccogli voce={voceRaccoglitore(d)} />}
                        <a href={fileUrl(d.file_url)} target="_blank" rel="noopener noreferrer" title="Apri">
                          <Button variant="outline" size="sm" className="h-7 w-7 p-0"><ExternalLink className="w-3.5 h-3.5" /></Button>
                        </a>
                        {gestisce && (
                          <Button variant="outline" size="sm" className="h-7 w-7 p-0" onClick={async () => { await api.post(`/equipment/documents/${d.id}/archive`); load(); }} title="Sposta in archivio">
                            <Archive className="w-3 h-3" />
                          </Button>
                        )}
                      </div>
                    </div>
                  ))}
                </div>
              </CardContent>
            </Card>
          ))}
          {DOC_TYPES.every(t => byType(t.value).length === 0) && (
            <p className="text-sm text-slate-500 text-center py-4">Nessun documento caricato</p>
          )}
        </TabsContent>

        <TabsContent value="maintenance" className="space-y-3">
          {costsByYear.length > 0 && (
            <div className="grid grid-cols-2 sm:grid-cols-3 gap-3">
              <Card className="border-slate-200">
                <CardContent className="p-4">
                  <div className="text-[10px] uppercase tracking-widest text-slate-500 mb-1">Costo totale storico</div>
                  <div className="font-display text-2xl font-black">{euro(totaleCosti)}</div>
                </CardContent>
              </Card>
              <Card className="border-slate-200">
                <CardContent className="p-4">
                  <div className="text-[10px] uppercase tracking-widest text-slate-500 mb-1">Interventi totali</div>
                  <div className="font-display text-2xl font-black">{totaleInterventi}</div>
                </CardContent>
              </Card>
              <Card className="border-slate-200">
                <CardContent className="p-4">
                  <div className="text-[10px] uppercase tracking-widest text-slate-500 mb-1">Anni coperti</div>
                  <div className="font-display text-2xl font-black">{costsByYear.length}</div>
                </CardContent>
              </Card>
            </div>
          )}
          {(eq.checklist_manutenzione?.length > 0 || eq.scadenze_esterne?.length > 0) && (
            <Card className="border-slate-200">
              <CardContent className="p-4 grid grid-cols-1 md:grid-cols-2 gap-4">
                {eq.checklist_manutenzione?.length > 0 && (
                  <div>
                    <h3 className="font-display font-bold text-sm mb-2">
                      Controlli interni
                      {eq.maintenance_interval_months && (
                        <span className="font-normal text-slate-500"> · ogni {eq.maintenance_interval_months} mesi</span>
                      )}
                    </h3>
                    <ul className="space-y-1">
                      {eq.checklist_manutenzione.map((c, i) => (
                        <li key={i} className="text-sm text-slate-700 flex gap-2">
                          <span className="text-orange-500 flex-shrink-0">·</span>
                          <span>{c}</span>
                        </li>
                      ))}
                    </ul>
                  </div>
                )}
                {eq.scadenze_esterne?.length > 0 && (
                  <div>
                    <h3 className="font-display font-bold text-sm mb-2">Manutenzioni esterne</h3>
                    <ul className="space-y-1">
                      {eq.scadenze_esterne.map((s, i) => (
                        <li key={i} className="text-sm text-slate-700 flex justify-between gap-2">
                          <span>{s.voce}</span>
                          <span className="text-slate-500 flex-shrink-0">{s.periodicita || "—"}</span>
                        </li>
                      ))}
                    </ul>
                  </div>
                )}
              </CardContent>
            </Card>
          )}
          {costsByYear.length > 0 && (
            <Card className="border-slate-200">
              <CardContent className="p-4">
                <h3 className="font-display font-bold text-sm mb-2">Costi manutenzione per anno</h3>
                <ResponsiveContainer width="100%" height={180}>
                  <BarChart data={costsByYear}>
                    <CartesianGrid strokeDasharray="3 3" stroke="#e2e8f0" />
                    <XAxis dataKey="year" tick={{ fontSize: 11 }} />
                    <YAxis tick={{ fontSize: 11 }} />
                    <Tooltip formatter={(v) => euro(v)} />
                    <Bar dataKey="total_cost" fill="#0067b2" radius={[4, 4, 0, 0]} className="cursor-pointer"
                         onClick={(payload) => { if (payload?.year) setYearDetail(payload.year); }} />
                  </BarChart>
                </ResponsiveContainer>
                <p className="text-[10px] text-slate-400 text-center mt-1">Clicca una barra per vedere gli interventi di quell'anno</p>
              </CardContent>
            </Card>
          )}
          {/* I due pulsanti non fanno la stessa cosa: il controllo interno è il
              giro delle verifiche previste dalla scheda, lo fa il personale
              dell'azienda e ha le sue spunte; l'intervento è una riparazione
              o una sostituzione, che una checklist non ce l'ha, e può essere
              fatto in casa o da un fornitore. */}
          {canLog && (
            <div className="flex gap-2 flex-wrap">
              <Button onClick={() => setShowControllo(true)} className="bg-orange-500 hover:bg-orange-600 text-white" data-testid="controllo-btn"
                      title="Il giro dei controlli previsti dalla scheda, fatto dal personale dell'azienda">
                <CheckCircle2 className="w-4 h-4 mr-2" />Esegui controllo interno
              </Button>
              <Button variant="outline" onClick={() => setShowMnt(true)} data-testid="add-mnt-btn"
                      title="Una riparazione, una revisione, una taratura: in casa o da un fornitore">
                <Plus className="w-4 h-4 mr-2" />Nuovo intervento di manutenzione
              </Button>
              {gestisce && (
                <Button variant="outline" onClick={() => setShowChecklist(true)} data-testid="edit-checklist-btn">
                  <Edit className="w-4 h-4 mr-2" />Modifica controlli
                </Button>
              )}
              {canImport && (
                <Button variant="outline" onClick={() => setShowImportDocx(true)} data-testid="import-docx-btn">
                  <FileUp className="w-4 h-4 mr-2" />Importa storico
                </Button>
              )}
            </div>
          )}
          {logs.length === 0
            ? <p className="text-sm text-slate-500">Nessun intervento registrato</p>
            : <Card className="border-slate-200"><CardContent className="p-0 overflow-x-auto">
                <table className="w-full text-sm min-w-[640px]">
                  <thead className="bg-slate-50 text-xs uppercase text-slate-500">
                    <tr><th className="text-left p-3">Data</th><th className="text-left p-3">Tipo</th><th className="text-left p-3">Fornitore / Chi</th><th className="text-left p-3">Descrizione</th>{vedeCosti && <th className="text-right p-3">Costo</th>}<th className="text-left p-3">Prossima</th>{(puoCorreggere || puoEliminare) && <th className="text-right p-3">&nbsp;</th>}</tr>
                  </thead>
                  <tbody>
                    {logs.map(l => (
                      <tr key={l.id} className={`border-t border-slate-100 ${l.esito === "anomalia" ? "bg-red-50/50" : ""}`}>
                        <td className="p-3">{l.date?.substring(0,10)}</td>
                        <td className="p-3"><Badge variant="outline">{l.type}</Badge></td>
                        <td className="p-3">{l.supplier || l.eseguito_da || "—"}</td>
                        <td className="p-3">
                          {l.description}
                          {(l.ore_contaore || l.km) && (
                            <span className="ml-2 text-xs text-slate-500">
                              {l.ore_contaore ? `${l.ore_contaore} ore` : ""}
                              {l.ore_contaore && l.km ? " · " : ""}
                              {l.km ? `${l.km} km` : ""}
                            </span>
                          )}
                          {/* Le anomalie sono il motivo per cui si fa il controllo:
                              vanno lette qui, non nascoste dietro un clic. */}
                          {l.checklist?.length > 0 && (
                            <div className="mt-1 space-y-0.5">
                              {l.checklist.filter(v => v.esito === "anomalia").map((v, i) => (
                                <div key={i} className="text-xs text-red-700 flex gap-1">
                                  <AlertTriangle className="w-3 h-3 flex-shrink-0 mt-0.5" />
                                  <span>{v.voce}{v.nota ? ` — ${v.nota}` : ""}</span>
                                </div>
                              ))}
                            </div>
                          )}
                          {l.note && <div className="text-xs text-slate-500 mt-1">{l.note}</div>}
                        </td>
                        {vedeCosti && (
                          <td className="p-3 text-right">
                            {/* Il preposto registra l'intervento esterno ma la
                                fattura non la vede: la cifra la mette qui chi
                                di dovere, altrimenti quella riga resterebbe
                                per sempre a zero. */}
                            {l.cost == null && l.type === "esterno" ? (
                              <button type="button" onClick={() => setImportoDa(l)}
                                      data-testid={`aggiungi-importo-${l.id}`}
                                      className="text-xs text-orange-600 hover:underline">
                                aggiungi importo
                              </button>
                            ) : euro(l.cost)}
                          </td>
                        )}
                        <td className="p-3">{l.next_due_date?.substring(0,10) || "—"}</td>
                        {(puoCorreggere || puoEliminare) && (
                          <td className="p-3">
                            <div className="flex items-center gap-1 justify-end">
                              {puoCorreggere && (
                                <Button variant="outline" size="sm" title="Correggi"
                                        data-testid={`modifica-intervento-${l.id}`}
                                        onClick={() => setCorreggi(l)}>
                                  <Edit className="w-3 h-3" />
                                </Button>
                              )}
                              {puoEliminare && (
                                <Button variant="outline" size="sm" title="Elimina"
                                        data-testid={`elimina-intervento-${l.id}`}
                                        onClick={() => eliminaIntervento(l)}>
                                  <Trash2 className="w-3 h-3 text-red-500" />
                                </Button>
                              )}
                            </div>
                          </td>
                        )}
                      </tr>
                    ))}
                  </tbody>
                </table>
              </CardContent></Card>
          }
        </TabsContent>

        <TabsContent value="archive">
          {archived.length === 0
            ? <p className="text-sm text-slate-500">Archivio vuoto</p>
            : <Card className="border-slate-200"><CardContent className="p-4 divide-y divide-slate-200">
                {archived.map(d => (
                  <div key={d.id} className="py-2 flex items-center justify-between gap-2">
                    <div className="flex-1 min-w-0">
                      <div className="text-sm font-medium">{d.title} <Badge variant="outline" className="ml-2 text-[10px]">{DOC_TYPES.find(t => t.value === d.doc_type)?.label || d.doc_type}</Badge></div>
                      <div className="text-xs text-slate-500">{d.filename} · archiviato {d.archived_at?.substring(0,10)}</div>
                    </div>
                    <div className="flex items-center gap-1 flex-shrink-0">
                      {d.file_url && <BottoneRaccogli voce={voceRaccoglitore(d)} />}
                      <a href={fileUrl(d.file_url)} target="_blank" rel="noopener noreferrer">
                        <Button variant="outline" size="sm"><ExternalLink className="w-3 h-3" /></Button>
                      </a>
                    </div>
                  </div>
                ))}
              </CardContent></Card>
          }
        </TabsContent>
      </Tabs>

      {showQR && (
        <Dialog open onOpenChange={() => setShowQR(false)}>
          <DialogContent className="text-center">
            <DialogHeader><DialogTitle>QR · {eq.identifier}</DialogTitle></DialogHeader>
            <QRBox percorso={`/equipment/${id}/qrcode`} titolo={eq.identifier}
                   sottotitolo={[eq.tipo, eq.brand, eq.model].filter(Boolean).join(" ")}
                   indirizzo={`${window.location.origin}/attrezzatura/${id}`}
                   nomeFile={`mezzo_${eq.identifier}`} />
          </DialogContent>
        </Dialog>
      )}
      {showUpload && <UploadDoc eqId={id} onClose={() => setShowUpload(false)} onSaved={() => { setShowUpload(false); load(); }} />}
      {showMnt && <MntForm eqId={id} onClose={() => setShowMnt(false)} onSaved={() => { setShowMnt(false); load(); }} />}
      {showImportDocx && <ImportMaintenanceDocxDialog eq={eq} onClose={() => setShowImportDocx(false)} onSaved={() => { setShowImportDocx(false); load(); }} />}
      {yearDetail && <YearDetailDialog eqId={id} year={yearDetail} onClose={() => setYearDetail(null)} />}
      {importoDa && <ImportoDialog log={importoDa} onClose={() => setImportoDa(null)}
                                   onSaved={() => { setImportoDa(null); load(); }} />}
      {/* Un controllo si corregge con la scheda del controllo, non con il
          modulo del nuovo intervento: quello delle spunte non sa niente, e
          riaprendolo le lasciava indietro. Gli altri interventi restano sul
          modulo di sempre. */}
      {correggi && (correggi.checklist?.length
        ? <ControlloInternoDialog eq={eq} log={correggi}
                                  onClose={() => setCorreggi(null)}
                                  onSaved={() => { setCorreggi(null); load(); }} />
        : <CorreggiInterventoDialog log={correggi} vedeCosti={vedeCosti}
                                    onClose={() => setCorreggi(null)}
                                    onSaved={() => { setCorreggi(null); load(); }} />)}
      {showEdit && <EditEqDialog eq={eq} cats={cats} onClose={() => setShowEdit(false)} onSubmit={onEditSubmit} />}
      {showControllo && <ControlloInternoDialog eq={eq} onClose={() => setShowControllo(false)} onSaved={() => { setShowControllo(false); load(); }} />}
      {showChecklist && <ModificaChecklistDialog eq={eq} onClose={() => setShowChecklist(false)} onSaved={() => { setShowChecklist(false); load(); }} />}
    </div>
  );
}

function UploadDoc({ eqId, onClose, onSaved }) {
  const [f, setF] = useState({ doc_type: "ce", title: "", issued_date: "", expires_date: "" });
  const scadenzaObbligatoria = f.doc_type === "revisione";
  const [file, setFile] = useState(null);
  const submit = async (e) => {
    e.preventDefault();
    if (!file) return toast.error("Seleziona un file");
    if (f.doc_type === "altro" && !f.title.trim()) {
      return toast.error("Scrivi che documento è: con «Altro» il titolo serve");
    }
    // Una revisione senza la data di scadenza non monitora niente: dice solo
    // che il mezzo una revisione l'ha fatta, non fino a quando può circolare.
    if (f.doc_type === "revisione" && !f.expires_date) {
      return toast.error("Metti la scadenza della revisione: è quella che fa scattare l'avviso");
    }
    const fd = new FormData();
    Object.entries(f).forEach(([k,v]) => v && fd.append(k, v));
    fd.append("file", file);
    try {
      await api.post(`/equipment/${eqId}/documents`, fd, { headers: { "Content-Type": "multipart/form-data" }});
      toast.success("Documento caricato"); onSaved();
    } catch (err) { toast.error(err.response?.data?.detail || "Errore"); }
  };
  return (
    <Dialog open onOpenChange={onClose}>
      <DialogContent>
        <DialogHeader><DialogTitle>Carica documento</DialogTitle></DialogHeader>
        <form onSubmit={submit} className="space-y-3">
          <div><Label>Tipo</Label>
            <select className="w-full h-10 border border-slate-200 rounded px-3" value={f.doc_type} onChange={(e)=>setF({...f, doc_type: e.target.value})}>
              {DOC_TYPES.map(t => <option key={t.value} value={t.value}>{t.label}</option>)}
            </select>
            <p className="text-[10px] text-slate-500 mt-1">
              {["ce", "libretto", "revisione"].includes(f.doc_type)
                ? "Caricandone uno nuovo, il precedente passa automaticamente in Archivio."
                : "Il documento si aggiunge a quelli che ci sono già."}
            </p>
          </div>
          <div>
            <Label>{f.doc_type === "altro" ? "Che documento è *" : "Titolo"}</Label>
            <Input value={f.title} onChange={(e)=>setF({...f, title: e.target.value})}
                   data-testid="titolo-documento"
                   placeholder={f.doc_type === "altro"
                     ? "es. Libretto di circolazione, Polizza RCA, Fattura d'acquisto"
                     : "opzionale"} />
          </div>
          <div className="grid grid-cols-2 gap-3">
            <div><Label>Data emissione</Label><Input type="date" value={f.issued_date} onChange={(e)=>setF({...f, issued_date: e.target.value})} /></div>
            <div>
              <Label>{scadenzaObbligatoria ? "Scadenza *" : "Scadenza"}</Label>
              <Input type="date" value={f.expires_date} data-testid="scadenza-documento"
                     required={scadenzaObbligatoria}
                     onChange={(e)=>setF({...f, expires_date: e.target.value})} />
              {scadenzaObbligatoria && (
                <p className="text-[10px] text-slate-500 mt-1">
                  È la data scritta sul tagliando: da lì parte l&apos;avviso, 60 giorni prima.
                </p>
              )}
            </div>
          </div>
          <div><Label>File *</Label><Input type="file" accept=".pdf,image/*" onChange={(e)=>setFile(e.target.files[0])} required /></div>
          <DialogFooter>
            <Button type="button" variant="outline" onClick={onClose}>Annulla</Button>
            <Button type="submit" className="bg-orange-500 hover:bg-orange-600 text-white">Carica</Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
}

/**
 * Correzione di un intervento gia' registrato.
 *
 * Serve soprattutto sugli storici importati dai libri cartacei, che portano
 * dentro le sbavature della carta: lo stesso intervento scritto due volte con
 * un trattino diverso, il fornitore su una copia e non sull'altra. Sistemarli
 * a mano e' piu' onesto che indovinare quali accorpare.
 */
function CorreggiInterventoDialog({ log, vedeCosti, onClose, onSaved }) {
  const [f, setF] = useState({
    date: (log.date || "").substring(0, 10),
    type: log.type || "interno",
    supplier: log.supplier || "",
    description: log.description || "",
    cost: log.cost ?? "",
    minuti: String(log.minuti || PASSO_MINUTI),
    next_due_date: (log.next_due_date || "").substring(0, 10),
  });
  const [busy, setBusy] = useState(false);

  const salva = async (e) => {
    e.preventDefault();
    setBusy(true);
    // Sull'intervento interno si manda il tempo: il costo lo rifà il server.
    const dati = f.type === "interno"
      ? { ...f, cost: null, minuti: parseInt(f.minuti, 10) }
      : { ...f, minuti: null,
          cost: f.cost === "" || f.cost === null ? null : parseFloat(f.cost) };
    try {
      await api.put(`/equipment/maintenance/${log.id}`, dati);
      toast.success("Intervento corretto");
      onSaved();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Errore");
    } finally { setBusy(false); }
  };

  return (
    <Dialog open onOpenChange={onClose}>
      <DialogContent className="max-h-[90vh] overflow-y-auto">
        <DialogHeader><DialogTitle>Correggi intervento</DialogTitle></DialogHeader>
        <form onSubmit={salva} className="space-y-3">
          <div className="grid grid-cols-2 gap-3">
            <div><Label>Data</Label>
              <Input type="date" value={f.date} required
                     onChange={(e)=>setF({...f, date: e.target.value})} /></div>
            <div><Label>Tipo</Label>
              <select className="w-full h-10 border border-slate-200 rounded px-3"
                      value={f.type} data-testid="correggi-tipo"
                      onChange={(e)=>setF({...f, type: e.target.value})}>
                <option value="interno">Interno</option>
                <option value="esterno">Esterno</option>
              </select>
            </div>
          </div>
          <div><Label>Fornitore / Tecnico</Label>
            <Input value={f.supplier}
                   onChange={(e)=>setF({...f, supplier: e.target.value})} /></div>
          <div><Label>Descrizione</Label>
            <Input value={f.description} required data-testid="correggi-descrizione"
                   onChange={(e)=>setF({...f, description: e.target.value})} /></div>

          {f.type === "interno" ? (
            <div>
              <Label>Tempo impiegato</Label>
              <select className="w-full h-10 border border-slate-200 rounded px-3"
                      value={f.minuti} data-testid="correggi-minuti"
                      onChange={(e)=>setF({...f, minuti: e.target.value})}>
                {FASCE_MINUTI.map(m => (
                  <option key={m} value={m}>{durataLeggibile(m)}</option>
                ))}
              </select>
              <p className="text-xs text-slate-500 mt-1">
                Il costo si rifà da qui, alla tariffa oraria dell&apos;azienda.
              </p>
            </div>
          ) : vedeCosti ? (
            <div>
              <Label>Costo (€)</Label>
              <Input type="number" step="0.01" value={f.cost}
                     data-testid="correggi-costo"
                     onChange={(e)=>setF({...f, cost: e.target.value})} />
              <p className="text-xs text-slate-500 mt-1">
                Lascia vuoto se la fattura non è ancora arrivata.
              </p>
            </div>
          ) : null}

          <div><Label>Prossima manutenzione</Label>
            <Input type="date" value={f.next_due_date}
                   onChange={(e)=>setF({...f, next_due_date: e.target.value})} /></div>

          <DialogFooter>
            <Button type="button" variant="outline" onClick={onClose}>Annulla</Button>
            <Button type="submit" disabled={busy} data-testid="salva-correzione"
                    className="bg-orange-500 hover:bg-orange-600 text-white">
              {busy ? "Salvataggio..." : "Salva"}
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
}

/**
 * La cifra della fattura, messa dopo su un intervento gia' registrato.
 *
 * Il preposto registra che il fornitore e' passato, ma la fattura non la vede:
 * senza questa finestra quella riga resterebbe per sempre senza importo, e i
 * costi del mezzo direbbero meno del vero.
 */
function ImportoDialog({ log, onClose, onSaved }) {
  const [cifra, setCifra] = useState("");
  const [busy, setBusy] = useState(false);

  const salva = async (e) => {
    e.preventDefault();
    setBusy(true);
    try {
      await api.put(`/equipment/maintenance/${log.id}/importo`,
                    { cost: parseFloat(cifra) });
      toast.success("Importo registrato");
      onSaved();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Errore");
    } finally { setBusy(false); }
  };

  return (
    <Dialog open onOpenChange={onClose}>
      <DialogContent>
        <DialogHeader><DialogTitle>Importo della fattura</DialogTitle></DialogHeader>
        <form onSubmit={salva} className="space-y-3">
          <div className="text-sm text-slate-600">
            {log.date?.substring(0, 10)} · {log.description}
            {log.supplier && <> · <strong>{log.supplier}</strong></>}
          </div>
          <div>
            <Label>Costo (€)</Label>
            <Input type="number" step="0.01" min="0" value={cifra} autoFocus required
                   data-testid="importo-fattura"
                   onChange={(e) => setCifra(e.target.value)} />
          </div>
          <DialogFooter>
            <Button type="button" variant="outline" onClick={onClose}>Annulla</Button>
            <Button type="submit" disabled={busy || cifra === ""}
                    className="bg-orange-500 hover:bg-orange-600 text-white">
              {busy ? "Salvataggio..." : "Salva"}
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
}

/**
 * L'intervento di manutenzione: una riparazione, una sostituzione, una
 * revisione, una taratura.
 *
 * Spunte non ne ha — quelle sono del controllo interno — ma può essere fatto
 * in casa o da un fornitore, e le due cose si contano in modo diverso: in
 * casa si dichiara il tempo e al valore ci pensa l'azienda con la sua
 * tariffa oraria, fuori c'è la fattura del fornitore.
 *
 * Anche la prossima scadenza cambia padrone: quella di un intervento fatto
 * fuori di solito la impone il fornitore — la revisione di un autocarro, la
 * taratura di uno strumento — e allora si scrive la data.
 */
function MntForm({ eqId, onClose, onSaved }) {
  const { user } = useAuth();
  const vedeCosti = vedeICosti(user);
  const today = new Date().toISOString().substring(0,10);
  const [f, setF] = useState({ equipment_id: eqId, date: today, type: "esterno",
                               supplier: "", description: "", cost: "",
                               minuti: String(PASSO_MINUTI), next_due_date: "" });
  const [cadenza, setCadenza] = useState("data");
  const [file, setFile] = useState(null);
  // La data che l'app propone: se si sceglie una cadenza si conta da qui,
  // altrimenti la si scrive, ed è il caso normale — la revisione e la
  // taratura hanno una scadenza che decide chi le esegue.
  const scadenza = cadenza === "data" ? f.next_due_date
                                      : fraMesi(f.date, Number(cadenza));
  const submit = async (e) => {
    e.preventDefault();
    // In casa si manda il tempo e basta: il costo lo mette il server, alla
    // tariffa oraria dell'azienda. Fuori c'è la fattura.
    const payload = {
      ...f,
      minuti: f.type === "interno" ? parseInt(f.minuti, 10) : null,
      cost: f.type === "interno" ? null : (f.cost ? parseFloat(f.cost) : null),
      cadenza_mesi: cadenza === "data" ? null : Number(cadenza),
      next_due_date: cadenza === "data" ? (f.next_due_date || null) : null,
    };
    try {
      await api.post("/equipment/maintenance", payload);
      if (file) {
        const fd = new FormData();
        fd.append("doc_type", "logbook");
        fd.append("title", `Intervento ${f.date} - ${f.description.substring(0,40)}`);
        fd.append("issued_date", f.date);
        fd.append("file", file);
        await api.post(`/equipment/${eqId}/documents`, fd, { headers: { "Content-Type": "multipart/form-data" }});
      }
      toast.success("Intervento registrato" + (file ? " + documento allegato" : ""));
      onSaved();
    } catch (err) { toast.error(err.response?.data?.detail || "Errore"); }
  };
  return (
    <Dialog open onOpenChange={onClose}>
      <DialogContent className="max-h-[90vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle>Nuovo intervento di manutenzione</DialogTitle>
        </DialogHeader>
        <form onSubmit={submit} className="space-y-3">
          <p className="text-xs text-slate-500 bg-slate-50 border border-slate-200 rounded p-2">
            Una riparazione, una sostituzione, una revisione, una taratura.
            Il giro dei controlli previsti dalla scheda si registra invece con
            <b> Esegui controllo interno</b>.
          </p>
          <div className="grid grid-cols-2 gap-3">
            <div><Label>Data</Label>
              <Input type="date" value={f.date} required data-testid="mnt-data"
                     onChange={(e)=>setF({...f, date: e.target.value})} /></div>
            <div><Label>Chi l&apos;ha fatto</Label>
              <div className="flex gap-2">
                {[["interno", "In casa"], ["esterno", "Fornitore"]].map(([v, et]) => (
                  <button key={v} type="button" onClick={()=>setF({...f, type: v})}
                          data-testid={"mnt-" + v}
                          className={`flex-1 h-10 rounded border text-sm ${
                            f.type === v
                              ? "bg-[#0067b2] text-white border-[#0067b2]"
                              : "bg-white border-slate-200 text-slate-600"}`}>
                    {et}
                  </button>
                ))}
              </div>
            </div>
          </div>
          <div>
            <Label>{f.type === "esterno" ? "Fornitore *" : "Chi l'ha eseguito"}</Label>
            <Input value={f.supplier} required={f.type === "esterno"}
                   data-testid="mnt-fornitore"
                   placeholder={f.type === "esterno"
                     ? "l'officina, il centro revisioni, il laboratorio"
                     : "il nome di chi ci ha messo le mani (facoltativo)"}
                   onChange={(e)=>setF({...f, supplier: e.target.value})} />
          </div>
          <div><Label>Descrizione intervento *</Label>
            <Input value={f.description} required data-testid="mnt-descrizione"
                   placeholder="cosa è stato fatto"
                   onChange={(e)=>setF({...f, description: e.target.value})} /></div>
          {f.type === "interno" ? (
            <div>
              <Label>Tempo impiegato</Label>
              <select className="w-full h-10 border border-slate-200 rounded px-3"
                      value={f.minuti} data-testid="minuti-intervento"
                      onChange={(e)=>setF({...f, minuti: e.target.value})}>
                {FASCE_MINUTI.map(m => (
                  <option key={m} value={m}>{durataLeggibile(m)}</option>
                ))}
              </select>
              <p className="text-xs text-slate-500 mt-1">
                Quanto ci è voluto, a mezz&apos;ore. Al resto pensa l&apos;azienda.
              </p>
            </div>
          ) : vedeCosti ? (
            <div>
              <Label>Importo (€)</Label>
              <Input type="number" step="0.01" value={f.cost} data-testid="mnt-importo"
                     onChange={(e)=>setF({...f, cost: e.target.value})} />
              <p className="text-xs text-slate-500 mt-1">
                L&apos;importo della fattura, se già la conosci. Puoi lasciarlo
                vuoto e metterlo dopo.
              </p>
            </div>
          ) : (
            <p className="text-xs text-slate-500 bg-slate-50 border border-slate-200
                          rounded p-2">
              Registra pure l&apos;intervento: alla fattura del fornitore pensa
              l&apos;ufficio.
            </p>
          )}
          <div>
            <Label>Prossima manutenzione</Label>
            <div className="flex gap-2 my-2 flex-wrap">
              {CADENZE.map(c => (
                <button type="button" key={c.mesi} data-testid={"mnt-cadenza-" + c.mesi}
                        onClick={() => setCadenza(c.mesi)}
                        className={`px-3 py-1.5 rounded text-xs border ${
                          cadenza === c.mesi
                            ? "bg-orange-500 text-white border-orange-500"
                            : "bg-white border-slate-200 hover:border-slate-300"}`}>
                  {c.etichetta}
                </button>
              ))}
              <button type="button" onClick={() => setCadenza("data")}
                      data-testid="mnt-cadenza-data"
                      className={`px-3 py-1.5 rounded text-xs border ${
                        cadenza === "data"
                          ? "bg-orange-500 text-white border-orange-500"
                          : "bg-white border-slate-200 hover:border-slate-300"}`}>
                In una data precisa
              </button>
            </div>
            {cadenza === "data" ? (
              <Input type="date" value={f.next_due_date} data-testid="mnt-prossima"
                     onChange={(e)=>setF({...f, next_due_date: e.target.value})} />
            ) : (
              <p className="text-xs text-slate-500">
                La prossima manutenzione cade il <b>{giorno(scadenza) || "—"}</b>.
              </p>
            )}
          </div>
          <div>
            <Label>Allega al Libro manutenzione (opzionale)</Label>
            <Input type="file" accept=".pdf,image/*" onChange={(e)=>setFile(e.target.files[0])} />
            <p className="text-[10px] text-slate-500 mt-1">Se caricato, sarà aggiunto al tab Documenti → Libro manutenzione.</p>
          </div>
          <DialogFooter>
            <Button type="button" variant="outline" onClick={onClose}>Annulla</Button>
            <Button type="submit" data-testid="mnt-registra"
                    className="bg-orange-500 hover:bg-orange-600 text-white">Registra</Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
}

function EditEqDialog({ eq, cats, onClose, onSubmit }) {
  const [f, setF] = React.useState({
    category: eq.category || "",
    identifier: eq.identifier || "",
    brand: eq.brand || "",
    model: eq.model || "",
    tipo: eq.tipo || "",
    year: eq.year || "",
    status: eq.status || "active",
    maintenance_interval_months: eq.maintenance_interval_months || 12,
    next_maintenance_date: eq.next_maintenance_date || "",
    notes: eq.notes || "",
    owner: eq.owner || "",
    utilizzatore_id: eq.utilizzatore_id || null,
    // La data di inserimento non si modifica da qui, ma si porta dietro:
    // il salvataggio riscrive tutta la scheda e la perderebbe.
    data_inserimento: eq.data_inserimento || null,
  });
  const submit = (e) => {
    e.preventDefault();
    onSubmit({ ...f,
      year: f.year ? parseInt(f.year) : null,
      maintenance_interval_months: parseInt(f.maintenance_interval_months) || null
    });
  };
  return (
    <Dialog open onOpenChange={onClose}>
      <DialogContent className="max-w-lg max-h-[90vh] overflow-y-auto">
        <DialogHeader><DialogTitle>Modifica attrezzatura</DialogTitle></DialogHeader>
        <form onSubmit={submit} className="space-y-3">
          <div><Label>Categoria</Label>
            <select className="w-full h-10 border border-slate-200 rounded px-3" value={f.category} onChange={(e)=>setF({...f, category: e.target.value})}>
              {cats.map(c => <option key={c}>{c}</option>)}
            </select>
          </div>
          <div><Label>Identificativo (targa/matricola) *</Label>
            <Input value={f.identifier} onChange={(e)=>setF({...f, identifier: e.target.value})} required />
          </div>
          <div className="grid grid-cols-2 gap-3">
            <div><Label>Marca</Label><Input value={f.brand} onChange={(e)=>setF({...f, brand: e.target.value})} /></div>
            <div><Label>Modello</Label><Input value={f.model} onChange={(e)=>setF({...f, model: e.target.value})} /></div>
          </div>
          <div className="grid grid-cols-2 gap-3">
            <div><Label>Tipo</Label><Input value={f.tipo} onChange={(e)=>setF({...f, tipo: e.target.value})} /></div>
            <div><Label>Anno</Label><Input type="number" value={f.year} onChange={(e)=>setF({...f, year: e.target.value})} /></div>
          </div>
          <div><Label>Proprietà</Label>
            <Input value={f.owner} placeholder="SGM IMPIANTI SRL"
                   onChange={(e)=>setF({...f, owner: e.target.value})} />
            <p className="text-xs text-slate-500 mt-1">
              Si compila solo se la macchina è di qualcun altro. Vuoto vale
              SGM IMPIANTI SRL.
            </p>
          </div>
          <CampoUtilizzatore categoria={f.category} value={f.utilizzatore_id}
                             onChange={(v) => setF({ ...f, utilizzatore_id: v })} />
          <div><Label>Stato</Label>
            <select className="w-full h-10 border border-slate-200 rounded px-3" value={f.status} onChange={(e)=>setF({...f, status: e.target.value})}>
              <option value="active">Attiva</option>
              <option value="maintenance">In manutenzione</option>
              <option value="dismissed">Dismessa</option>
              <option value="stolen">Rubata</option>
              <option value="scrapped">Smaltita</option>
              <option value="sold">Venduta</option>
            </select>
          </div>
          <div className="grid grid-cols-2 gap-3">
            <div><Label>Cadenza (mesi)</Label>
              <Input type="number" value={f.maintenance_interval_months} onChange={(e)=>setF({...f, maintenance_interval_months: e.target.value})} />
            </div>
            <div><Label>Prossima manutenzione</Label>
              <Input type="date" value={f.next_maintenance_date || ""} onChange={(e)=>setF({...f, next_maintenance_date: e.target.value})} />
            </div>
          </div>
          <div><Label>Note</Label>
            <Input value={f.notes} onChange={(e)=>setF({...f, notes: e.target.value})} />
          </div>
          <DialogFooter>
            <Button type="button" variant="outline" onClick={onClose}>Annulla</Button>
            <Button type="submit" className="bg-orange-500 hover:bg-orange-600 text-white">Salva</Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
}

function ImportMaintenanceDocxDialog({ eq, onClose, onSaved }) {
  const ref = React.useRef(null);
  const [file, setFile] = useState(null);
  const [busy, setBusy] = useState(false);
  const [report, setReport] = useState(null);
  const [replacePrevious, setReplacePrevious] = useState(false);

  const downloadTemplate = async () => {
    try {
      const r = await authFetch(`/equipment/${eq.id}/maintenance/import-template`);
      if (!r.ok) throw new Error();
      const b = await r.blob();
      const u = window.URL.createObjectURL(b);
      const a = document.createElement("a");
      a.href = u; a.download = `modello_manutenzione_${eq.identifier}.xlsx`; a.click();
      window.URL.revokeObjectURL(u);
    } catch { toast.error("Errore nel download del modello"); }
  };

  const submit = async () => {
    if (!file) { toast.error("Seleziona il file Excel"); return; }
    setBusy(true);
    const fd = new FormData();
    fd.append("file", file);
    fd.append("replace_previous", replacePrevious ? "true" : "false");
    try {
      const { data } = await api.post(`/equipment/${eq.id}/maintenance/import-xlsx`, fd,
        { headers: { "Content-Type": "multipart/form-data" } });
      setReport(data);
      if (data.imported > 0) {
        toast.success(`${data.imported} interventi importati${data.removed_previous > 0 ? ` (${data.removed_previous} vecchi rimossi)` : ""}`);
      }
      onSaved();
    } catch (e) { toast.error(e.response?.data?.detail || "Errore durante l'importazione"); }
    finally { setBusy(false); }
  };

  return (
    <Dialog open onOpenChange={onClose}>
      <DialogContent className="max-w-lg max-h-[90vh] overflow-y-auto">
        <DialogHeader><DialogTitle>Importa storico manutenzione · {eq.identifier}</DialogTitle></DialogHeader>
        {!report ? (
          <div className="space-y-3">
            <div className="p-3 bg-slate-50 border border-slate-200 rounded space-y-2">
              <div className="text-sm font-semibold">1 · Scarica il modello per questo mezzo</div>
              <p className="text-xs text-slate-600">
                Colonne separate per data, tipo, fornitore, descrizione, costo — così ogni campo entra
                pulito, senza dover indovinare cosa è fornitore e cosa è descrizione.
              </p>
              <Button variant="outline" size="sm" onClick={downloadTemplate}>
                <Download className="w-3 h-3 mr-1" /> Scarica modello .xlsx
              </Button>
            </div>
            <div>
              <Label>2 · Carica il file compilato</Label>
              <input ref={ref} type="file" accept=".xlsx,.xlsm" hidden onChange={(e) => setFile(e.target.files?.[0] || null)} />
              <Button variant="outline" className="w-full mt-1 justify-start" onClick={() => ref.current?.click()}>
                <FileUp className="w-4 h-4 mr-2 flex-shrink-0" />
                <span className="truncate">{file ? file.name : "Seleziona file .xlsx..."}</span>
              </Button>
            </div>
            <label className="flex items-start gap-2 p-2 bg-slate-50 border border-slate-200 rounded cursor-pointer">
              <input type="checkbox" className="mt-0.5" checked={replacePrevious} onChange={(e) => setReplacePrevious(e.target.checked)} />
              <span className="text-xs text-slate-600">
                Sostituisci un import precedente per questo mezzo (evita i doppioni se ricarichi un
                file corretto). Gli interventi inseriti manualmente non vengono toccati.
              </span>
            </label>
            <DialogFooter>
              <Button variant="outline" onClick={onClose}>Annulla</Button>
              <Button onClick={submit} disabled={busy || !file} className="bg-orange-500 hover:bg-orange-600 text-white">
                {busy ? "Importazione..." : "Importa"}
              </Button>
            </DialogFooter>
          </div>
        ) : (
          <div className="space-y-3">
            <div className="grid grid-cols-2 gap-2">
              <div className="rounded border border-emerald-200 bg-emerald-50 p-3">
                <div className="text-[10px] uppercase text-emerald-700">Importati</div>
                <div className="font-display text-2xl font-black text-emerald-700">{report.imported}</div>
              </div>
              <div className="rounded border border-amber-200 bg-amber-50 p-3">
                <div className="text-[10px] uppercase text-amber-700">Scartati</div>
                <div className="font-display text-2xl font-black text-amber-700">{report.skipped}</div>
              </div>
            </div>
            <div className="border border-slate-200 rounded max-h-72 overflow-y-auto">
              <table className="w-full text-xs">
                <thead className="bg-slate-50 sticky top-0"><tr>
                  <th className="text-left p-2">Data</th><th className="text-left p-2">Esito</th><th className="text-left p-2">Descrizione</th>
                </tr></thead>
                <tbody>
                  {report.details.map((d, i) => (
                    <tr key={i} className="border-t border-slate-100">
                      <td className="p-2 font-mono">{d.raw_date}</td>
                      <td className="p-2">
                        {d.status === "importato"
                          ? <Badge className="bg-emerald-100 text-emerald-700 border-0 text-[10px]"><CheckCircle2 className="w-3 h-3 mr-1" />ok</Badge>
                          : <Badge variant="destructive" className="text-[10px]"><XCircle className="w-3 h-3 mr-1" />{d.reason}</Badge>}
                      </td>
                      <td className="p-2 text-slate-500">{d.description || "—"}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
            <DialogFooter>
              <Button onClick={onClose} className="bg-orange-500 hover:bg-orange-600 text-white">Chiudi</Button>
            </DialogFooter>
          </div>
        )}
      </DialogContent>
    </Dialog>
  );
}

function YearDetailDialog({ eqId, year, onClose }) {
  // Chi vede gli importi lo decide il ruolo: qui il dialogo e' un componente
  // a se', e la variabile del padre non arriva. Senza questa riga la finestra
  // si bloccava su «Caricamento...» appena arrivavano i dati.
  const { user } = useAuth();
  const vedeCosti = vedeICosti(user);
  const [logs, setLogs] = useState(null);

  useEffect(() => {
    api.get(`/equipment/${eqId}/maintenance/year/${year}`).then(({ data }) => setLogs(data)).catch(() => toast.error("Errore caricamento"));
  }, [eqId, year]);

  const total = (logs || []).reduce((s, l) => s + (l.cost || 0), 0);

  return (
    <Dialog open onOpenChange={onClose}>
      <DialogContent className="max-w-lg max-h-[90vh] overflow-y-auto">
        <DialogHeader><DialogTitle>Interventi {year}</DialogTitle></DialogHeader>
        {!logs ? <p className="text-sm text-slate-500 py-6 text-center">Caricamento...</p> : (
          <div className="space-y-2">
            <div className="text-sm text-slate-600">
              {logs.length} interventi
              {vedeCosti && <> · totale <strong>{euro(total)}</strong></>}
            </div>
            {logs.map(l => (
              <div key={l.id} className="p-2 border border-slate-200 rounded text-sm">
                <div className="flex items-center justify-between">
                  <span className="font-semibold">{l.date?.substring(0, 10)}</span>
                  <Badge variant="outline" className="text-[10px]">{l.type}</Badge>
                </div>
                <div className="text-slate-600">{l.description}</div>
                <div className="flex items-center justify-between text-xs text-slate-400 mt-1">
                  <span>{l.supplier || "—"}</span>
                  {vedeCosti && <span>{euro(l.cost)}</span>}
                </div>
              </div>
            ))}
          </div>
        )}
      </DialogContent>
    </Dialog>
  );
}